import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@ecom/ui/card';
import { Button } from '@ecom/ui/button';
import { Input } from '@ecom/ui/input';
import { Label } from '@ecom/ui/label';
import { trpc } from '@/index';
import type { MarketplaceType } from '@ecom/core';

export const Route = createFileRoute('/(app)/shops/new')({
  component: NewShopPage,
});

const MARKETPLACES: Array<{
  type: MarketplaceType;
  name: string;
  desc: string;
  emoji: string;
  needsShopDomain: boolean;
}> = [
  { type: 'amazon',      name: 'Amazon',      desc: 'SP-API · 6 regions', emoji: '🛒', needsShopDomain: false },
  { type: 'shopify',     name: 'Shopify',     desc: 'Admin API',          emoji: '🛍️', needsShopDomain: true },
  { type: 'tiktok_shop', name: 'TikTok Shop', desc: 'OAuth 2.0',          emoji: '🎵', needsShopDomain: false },
];

const REGIONS = ['US', 'UK', 'DE', 'JP', 'FR', 'IT', 'CA', 'SG'];

export function NewShopPage() {
  const navigate = useNavigate();
  const [region, setRegion] = useState('US');
  const [shopDomain, setShopDomain] = useState('');
  const createUrl = trpc.shop.createAuthorizationUrl.useMutation();

  async function startAuth(type: MarketplaceType, needsShopDomain: boolean) {
    if (needsShopDomain && !shopDomain) return;
    const { authUrl } = await createUrl.mutateAsync({
      marketplaceType: type,
      region,
      shop: needsShopDomain ? shopDomain : undefined,
    });
    window.location.href = authUrl;
  }

  return (
    <div className="p-6 max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Connect a shop</h1>
        <p className="text-muted-foreground">
          Pick a sales channel and complete OAuth authorization.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Region</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {REGIONS.map((r) => (
              <Button
                key={r}
                variant={region === r ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRegion(r)}
              >
                {r}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Shopify shop domain</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Input
              placeholder="my-brand"
              value={shopDomain}
              onChange={(e) => setShopDomain(e.target.value)}
            />
            <span className="text-muted-foreground text-sm">.myshopify.com</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-3 gap-4">
        {MARKETPLACES.map((m) => (
          <Card key={m.type} className="p-6 space-y-4 hover:shadow-md transition">
            <div className="text-3xl">{m.emoji}</div>
            <div>
              <div className="font-semibold">{m.name}</div>
              <div className="text-xs text-muted-foreground">{m.desc}</div>
            </div>
            <Button
              className="w-full"
              loading={createUrl.isPending && createUrl.variables?.marketplaceType === m.type}
              disabled={m.needsShopDomain && !shopDomain}
              onClick={() => startAuth(m.type, m.needsShopDomain)}
            >
              Connect
            </Button>
          </Card>
        ))}
      </div>

      <Button variant="ghost" onClick={() => navigate({ to: '/shops' })}>
        ← Back
      </Button>
    </div>
  );
}
